<script setup lang="ts">
// WTA-style breadcrumb bar (e.g. "Home › Rankings › Singles"): sits on the green
// page wash just under the floating nav. Auto-derives the trail from the current
// route via a parent map, and lets a page override the leaf label (player name,
// active category) through the shared `breadcrumbLeaf` ref.
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { breadcrumbLeaf, setBreadcrumbLeaf } from '@/composables/breadcrumb'

interface Crumb {
  label: string
  to?: string
}

const route = useRoute()

// Detail routes hang off their listing page.
const parents: Record<string, { label: string; to: string }> = {
  'player-profile': { label: 'Players', to: '/players' },
  'tournament-detail': { label: 'Tournaments', to: '/tournaments' },
  'court-detail': { label: 'Courts', to: '/courts' },
  'club-detail': { label: 'Clubs', to: '/clubs' },
  'news-detail': { label: 'News', to: '/news' },
}

// Clear any stale page-supplied leaf whenever the route changes.
watch(
  () => route.fullPath,
  () => setBreadcrumbLeaf(null),
)

const crumbs = computed<Crumb[]>(() => {
  const name = String(route.name ?? '')
  const list: Crumb[] = [{ label: 'Home', to: '/' }]
  const parent = parents[name]
  const title = (route.meta.title as string | undefined) ?? ''
  const leaf = breadcrumbLeaf.value

  if (parent) {
    // Detail page → Home › <Listing> › <entity name or generic title>.
    list.push({ label: parent.label, to: parent.to })
    const current = leaf ?? title
    if (current) list.push({ label: current })
  } else if (leaf && title) {
    // Listing page with an active filter → Home › <Page> › <filter>.
    list.push({ label: title, to: route.path })
    list.push({ label: leaf })
  } else if (title) {
    // Plain listing page → Home › <Page>.
    list.push({ label: title })
  }
  return list
})
</script>

<template>
  <nav class="cpa-crumbs" aria-label="Breadcrumb">
    <div class="cpa-crumbs__inner">
      <template v-for="(crumb, i) in crumbs" :key="i">
        <RouterLink
          v-if="crumb.to && i < crumbs.length - 1"
          :to="crumb.to"
          class="cpa-crumbs__link"
        >
          {{ crumb.label }}
        </RouterLink>
        <span v-else class="cpa-crumbs__current" aria-current="page">
          {{ crumb.label }}
        </span>
        <v-icon
          v-if="i < crumbs.length - 1"
          icon="mdi-chevron-right"
          size="16"
          class="cpa-crumbs__sep"
        />
      </template>
    </div>
  </nav>
</template>

<style scoped>
/* Full-width band so the inner column can align with page content (v-container).
   NB: never use `margin: 0 auto` on this <nav> — it's a flex item of the column
   layout, where auto side-margins shrink-wrap + center it instead of filling. */
.cpa-crumbs {
  width: 100%;
}
.cpa-crumbs__inner {
  max-width: 1200px;
  margin: 0 auto;
  /* 16px sides match v-container so "Home" lines up with the content below. */
  padding: 14px 16px 20px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  font-family: var(--cpa-font-body);
  font-size: 0.85rem;
}
.cpa-crumbs__link {
  color: var(--cpa-primary-700);
  font-weight: 600;
  text-decoration: none;
  padding: 3px 6px;
  border-radius: 6px;
  opacity: 0.78;
  transition: opacity var(--cpa-dur) var(--cpa-ease), background var(--cpa-dur) var(--cpa-ease);
}
.cpa-crumbs__link:hover {
  opacity: 1;
  background: rgba(43, 10, 64, 0.06);
}
.cpa-crumbs__current {
  color: var(--cpa-primary-800);
  font-weight: 800;
  padding: 3px 6px;
}
.cpa-crumbs__sep {
  color: var(--cpa-primary-700);
  opacity: 0.4;
}
.v-theme--cpaDark .cpa-crumbs__link,
.v-theme--cpaDark .cpa-crumbs__current,
.v-theme--cpaDark .cpa-crumbs__sep {
  color: #fff;
}
</style>
